import { api, CustomAxiosRequestConfig } from "@/shared/api/client";
import {
  SupplierGetDTO,
  SupplierCreateDTO,
  SupplierUpdateDTO,
  SupplierQueryFilter,
  PageSuppliersGetDTO,
} from "@/shared/types/admin/supplier";
import { queryClient } from "@/shared/lib/queryClient";
import { queryKeys } from "@/shared/lib/queryKeys";

// A lista de fornecedores dos formulários (useSuppliers) fica em cache 15 min: sem isto, um
// fornecedor criado aqui só aparecia no lançamento de custo depois disso.
const invalidateSuppliers = () => queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });

/** Tipo do perfil em maiúsculas, como o systemProfileService manda (o filtro por tipo usa SUPPLIER). */
const withUpperType = <T extends { systemProfile?: { type?: string } }>(data: T): T =>
  data.systemProfile?.type
    ? { ...data, systemProfile: { ...data.systemProfile, type: data.systemProfile.type.toUpperCase() } }
    : data;

export const supplierService = {
  getAll: async (params: {
    page: number;
    size: number;
    sort?: string[];
    filter?: SupplierQueryFilter;
  }): Promise<PageSuppliersGetDTO> => {
    const response = await api.get<PageSuppliersGetDTO>("/suppliers", {
      params: {
        page: params.page - 1,
        size: params.size,
        sort: params.sort,
        name: params.filter?.name,
        type: params.filter?.type,
        status: params.filter?.status,
        active: params.filter?.active,
      },
    });
    return response.data;
  },

  getById: async (id: string): Promise<SupplierGetDTO> => {
    const response = await api.get<SupplierGetDTO>(`/suppliers/${id}`, {
      skipErrorToast: true,
    });
    return response.data;
  },

  create: async (data: SupplierCreateDTO): Promise<SupplierGetDTO> => {
    const response = await api.post<SupplierGetDTO>("/suppliers", withUpperType(data));
    void invalidateSuppliers();
    return response.data;
  },

  update: async (id: string, data: SupplierUpdateDTO): Promise<SupplierGetDTO> => {
    const response = await api.put<SupplierGetDTO>(`/suppliers/${id}`, withUpperType(data));
    void invalidateSuppliers();
    return response.data;
  },

  disable: async (id: string): Promise<void> => {
    await api.patch(`/suppliers/${id}/disable`);
    void invalidateSuppliers();
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/suppliers/${id}`);
    void invalidateSuppliers();
  },
};
